import { Button, ConfirmDialog, FormError } from "@simplicity/ui";
import { useState } from "react";
import { RunStatusBadge } from "./OnsiteBadges";
import {
  approveConfirmText,
  describeCounts,
  planOf,
  runAgentLabel,
  runOutputs,
} from "./onsiteModel";
import { PlanChanges, PlanLogLink } from "./PlanChanges";
import { describeUsers, runActionLabel, runUsersOf, SFTP_USERS_ACTION } from "./sftpUsersModel";
import type { OnsiteAgent, OnsiteRun, OutputValue, PlanSummary } from "./types";
import { useApproveApply } from "./useOnsite";

function Outputs({ outputs }: { outputs: Record<string, OutputValue> }) {
  const entries = Object.entries(outputs);
  if (entries.length === 0) return null;
  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold text-foreground">Outputs</h4>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 font-mono text-xs">
        {entries.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-muted-foreground">{key}</dt>
            <dd className="break-all text-foreground">{value === null ? "—" : String(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ApproveApply({
  run,
  plan,
  workspaceId,
  targetLabel,
  blocked,
}: {
  run: OnsiteRun;
  plan: PlanSummary;
  workspaceId: string;
  targetLabel: string;
  blocked: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const approve = useApproveApply(workspaceId);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setConfirming(true)} disabled={blocked || approve.isPending}>
          {approve.isPending ? "Approving..." : "Approve apply"}
        </Button>
        {blocked && (
          <span className="text-xs text-muted-foreground">
            Another run is pending or running; approve once it finishes.
          </span>
        )}
      </div>
      {approve.error && <FormError assertive>{approve.error.message}</FormError>}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Approve plan #${run.id}`}
        description={approveConfirmText(plan, targetLabel)}
        onConfirm={() => approve.mutate(run.id)}
        options={{ confirmLabel: "Approve", destructive: plan.replace + plan.delete > 0 }}
      />
    </div>
  );
}

function pendingText(run: OnsiteRun): string {
  if (run.status === "pending")
    return "Waiting for the agent to claim this run at its next check-in.";
  if (run.status === "running") return "The agent is running this now.";
  return run.action === SFTP_USERS_ACTION
    ? "This run reported no users."
    : "This run reported no plan.";
}

function RunBody({ run, plan }: { run: OnsiteRun; plan: PlanSummary | null }) {
  if (plan) return <PlanChanges plan={plan} />;
  const users = runUsersOf(run);
  if (users) return <p className="text-sm text-foreground">{describeUsers(users)}</p>;
  return <p className="text-sm text-muted-foreground">{pendingText(run)}</p>;
}

export function RunDetail({
  run,
  approvable,
  blocked,
  workspaceId,
  targetLabel,
  agent,
}: {
  run: OnsiteRun;
  approvable: boolean;
  blocked: boolean;
  workspaceId: string;
  targetLabel: string;
  /** The workspace's agent, whose build names the digest the run was stamped with. */
  agent: OnsiteAgent;
}) {
  const plan = planOf(run);
  const agentLabel = runAgentLabel(run.agentVersion, agent);
  return (
    <section className="form-card space-y-4" aria-label={`Run ${run.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-base font-semibold text-foreground">
          Run #{run.id} · {runActionLabel(run)}
        </h3>
        <RunStatusBadge status={run.status} />
        {plan && <span className="text-sm text-muted-foreground">{describeCounts(plan)}</span>}
        {agentLabel && <span className="text-sm text-muted-foreground">Agent: {agentLabel}</span>}
      </div>
      {approvable && plan && (
        <ApproveApply
          run={run}
          plan={plan}
          workspaceId={workspaceId}
          targetLabel={targetLabel}
          blocked={blocked}
        />
      )}
      <RunBody run={run} plan={plan} />
      <PlanLogLink run={run} />
      <Outputs outputs={runOutputs(run)} />
    </section>
  );
}
